import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, X, Send, Volume2, VolumeX, Phone, Mic, MicOff, Loader2, Navigation } from 'lucide-react';
import api from '../utils/api';

const VoiceAgentWidget = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [isListening, setIsListening] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    fetchSettings();
    // Generate session ID
    setSessionId(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
    
    // Initialize speech recognition
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'hi-IN';
      
      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputText(transcript);
        setIsListening(false);
      };
      
      recognitionRef.current.onerror = () => {
        setIsListening(false);
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/voice-agent/settings');
      setSettings(res.data);
      
      // Check public visibility settings for Samadhan
      try {
        const visibilityRes = await api.get('/admin/ai-control/public/avatar-visibility');
        const visibility = visibilityRes.data;
        
        // If emergency shutdown or samadhan not visible, hide widget
        if (visibility.emergency_shutdown || !visibility.samadhan_avatar_visible || !visibility.samadhan_enabled) {
          setSettings(prev => ({
            ...prev,
            is_enabled: !visibility.emergency_shutdown && visibility.samadhan_enabled,
            show_widget: !visibility.emergency_shutdown && visibility.samadhan_avatar_visible
          }));
        }
      } catch (e) {
        console.log('Could not fetch visibility settings:', e);
      }
      
      // Add welcome message
      if (res.data.welcome_message) {
        setMessages([{
          role: 'assistant',
          content: res.data.welcome_message
        }]);
      }
    } catch (error) {
      console.error('Failed to fetch voice agent settings:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Preload voices - more robust loading
  useEffect(() => {
    const loadVoices = () => {
      if (window.speechSynthesis) {
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          setAvailableVoices(voices);
          console.log('Voices loaded:', voices.length);
        }
      }
    };
    
    // Load immediately
    loadVoices();
    
    // Also listen for voiceschanged event (needed for Chrome)
    if (window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
    
    // Fallback: try loading after a delay
    const timer = setTimeout(loadVoices, 500);
    
    return () => {
      clearTimeout(timer);
      if (window.speechSynthesis) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  const findBestVoice = (voices) => {
    if (!voices || voices.length === 0) return null;
    
    // Priority order for finding the best voice
    const voicePriorities = [
      // 1. Google Hindi Female
      (v) => v.name.toLowerCase().includes('google') && v.lang.includes('hi') && v.name.toLowerCase().includes('female'),
      // 2. Any Hindi Female
      (v) => v.lang.includes('hi') && v.name.toLowerCase().includes('female'),
      // 3. Google Hindi
      (v) => v.name.toLowerCase().includes('google') && v.lang.includes('hi'),
      // 4. Any Hindi voice
      (v) => v.lang.includes('hi') || v.lang === 'hi-IN',
      // 5. Indian English
      (v) => v.lang.includes('en-IN'),
      // 6. Any female English
      (v) => v.name.toLowerCase().includes('female') && v.lang.includes('en'),
      // 7. Any English
      (v) => v.lang.includes('en'),
      // 8. First available
      () => true
    ];
    
    for (const check of voicePriorities) {
      const found = voices.find(check);
      if (found) return found;
    }
    
    return voices[0];
  };

  const speak = (text) => {
    if (!voiceEnabled || !window.speechSynthesis) {
      console.log('Speech disabled or not supported');
      return;
    }
    
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = settings?.voice_lang || 'hi-IN';
    utterance.rate = settings?.voice_rate || 0.9;
    utterance.pitch = settings?.voice_pitch || 1.0;
    utterance.volume = 1.0;
    
    // Get voices - use cached or fetch fresh
    let voices = availableVoices.length > 0 ? availableVoices : window.speechSynthesis.getVoices();
    
    // If still no voices, try with delay
    if (voices.length === 0) {
      console.log('No voices available, retrying...');
      setTimeout(() => {
        voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          setAvailableVoices(voices);
          executeSpeak(utterance, voices);
        } else {
          // Last resort: speak without voice selection
          executeSpeak(utterance, []);
        }
      }, 200);
      return;
    }
    
    executeSpeak(utterance, voices);
  };

  const executeSpeak = (utterance, voices) => {
    const selectedVoice = findBestVoice(voices);
    
    if (selectedVoice) {
      utterance.voice = selectedVoice;
      utterance.lang = selectedVoice.lang;
      console.log('Using voice:', selectedVoice.name, selectedVoice.lang);
    }
    
    utterance.onstart = () => {
      console.log('Speech started');
      setIsSpeaking(true);
    };
    utterance.onend = () => {
      console.log('Speech ended');
      setIsSpeaking(false);
    };
    utterance.onerror = (e) => {
      console.error('Speech error:', e.error);
      setIsSpeaking(false);
    };
    
    try {
      // Chrome fix: resume if paused
      if (window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
      }
      window.speechSynthesis.speak(utterance);
    } catch (e) {
      console.error('Failed to speak:', e);
      setIsSpeaking(false);
    }
  };

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  const startListening = () => {
    if (recognitionRef.current) {
      setIsListening(true);
      recognitionRef.current.start();
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const sendMessage = async () => {
    if (!inputText.trim() || loading) return;
    
    const userMessage = inputText.trim();
    setInputText('');
    
    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    
    setLoading(true);
    try {
      const res = await api.post('/voice-agent/chat', {
        message: userMessage,
        session_id: sessionId
      });
      
      const assistantMessage = res.data.response;
      setMessages(prev => [...prev, { role: 'assistant', content: assistantMessage }]);
      
      // Check if there's a navigation intent
      if (res.data.navigation?.should_navigate) {
        const { route, page_name } = res.data.navigation;
        setPendingNavigation({ route, page_name });
        
        // Auto-navigate after speaking completes (or after 2 seconds if voice disabled)
        const navDelay = voiceEnabled ? 3000 : 1500;
        setTimeout(() => {
          if (route) {
            navigate(route);
            setIsOpen(false);
            setPendingNavigation(null);
          }
        }, navDelay);
      }
      
      // Speak the response
      if (voiceEnabled) {
        speak(assistantMessage);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'माफ़ करें, कुछ गड़बड़ हो गई। कृपया दोबारा कोशिश करें।' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateNow = () => {
    if (pendingNavigation?.route) {
      navigate(pendingNavigation.route);
      setIsOpen(false);
      setPendingNavigation(null);
      stopSpeaking();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const openWhatsApp = () => {
    const phone = settings?.whatsapp_number || '919579369830';
    window.open(`https://wa.me/${phone}`, '_blank');
  };

  // Don't render if widget is disabled
  if (settings && !settings.is_enabled) return null;
  if (settings && !settings.show_widget) return null;

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 right-6 z-50 w-16 h-16 bg-gradient-to-br from-primary to-amber-600 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform"
          data-testid="voice-agent-button"
        >
          <img 
            src={settings?.agent_photo || '/priya-avatar.png'} 
            alt="Priya"
            className="w-14 h-14 rounded-full object-cover border-2 border-white"
          />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] h-[500px] max-h-[calc(100vh-6rem)] bg-card border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="voice-agent-chat">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary/20 to-amber-600/20 p-4 flex items-center gap-3 border-b border-white/10">
            <div className="relative">
              <img 
                src={settings?.agent_photo || '/priya-avatar.png'} 
                alt="Priya"
                className="w-12 h-12 rounded-full object-cover border-2 border-primary"
              />
              <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-card"></span>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg">{settings?.agent_name || 'Priya'}</h3>
              <p className="text-xs text-muted-foreground">AI Assistant • Online</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setVoiceEnabled(!voiceEnabled)}
                className={`p-2 rounded-lg transition-colors ${voiceEnabled ? 'bg-primary/20 text-primary' : 'bg-white/5 text-muted-foreground'}`}
                title={voiceEnabled ? 'Mute voice' : 'Enable voice'}
              >
                {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
              <button
                onClick={openWhatsApp}
                className="p-2 rounded-lg bg-green-500/20 text-green-500 hover:bg-green-500/30"
                title="Contact on WhatsApp"
              >
                <Phone className="w-4 h-4" />
              </button>
              <button
                onClick={() => { setIsOpen(false); stopSpeaking(); }}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-2xl ${
                  msg.role === 'user' 
                    ? 'bg-primary text-primary-foreground rounded-br-sm' 
                    : 'bg-white/10 rounded-bl-sm'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white/10 p-3 rounded-2xl rounded-bl-sm">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm text-muted-foreground">Priya is typing...</span>
                  </div>
                </div>
              </div>
            )}
            {isSpeaking && (
              <div className="flex justify-center">
                <button
                  onClick={stopSpeaking}
                  className="px-3 py-1 bg-primary/20 text-primary rounded-full text-xs flex items-center gap-1"
                >
                  <Volume2 className="w-3 h-3 animate-pulse" />
                  Speaking... (tap to stop)
                </button>
              </div>
            )}
            {pendingNavigation && (
              <div className="flex justify-center">
                <button
                  onClick={handleNavigateNow}
                  className="px-4 py-2 bg-green-500/20 text-green-400 rounded-full text-sm flex items-center gap-2 hover:bg-green-500/30 transition-colors animate-pulse"
                  data-testid="navigate-now-btn"
                >
                  <Navigation className="w-4 h-4" />
                  Go Now →
                </button>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-2">
              <button
                onClick={isListening ? stopListening : startListening}
                className={`p-3 rounded-xl transition-colors ${
                  isListening 
                    ? 'bg-red-500 text-white animate-pulse' 
                    : 'bg-white/5 hover:bg-white/10'
                }`}
                title={isListening ? 'Stop listening' : 'Voice input'}
              >
                {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type or speak your message..."
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm"
                disabled={loading}
              />
              <button
                onClick={sendMessage}
                disabled={!inputText.trim() || loading}
                className="p-3 bg-primary text-primary-foreground rounded-xl disabled:opacity-50"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VoiceAgentWidget;
